import { useCallback, useState } from "react"
import type {
  DealDrawer as DealDrawerData,
  DealDrawerRosterContact,
} from "@/types/v2/conversation"
import { getCsrfToken } from "@/lib/csrf"
import { replaceDealProp } from "@/lib/deal-prop-update"
import type { DealPropKey } from "@/lib/deal-prop-update"

// Mutation hook for the deal contacts roster (CON-260).
// Replaces `useContactSwap` — manages the full roster
// instead of a single swap.
//
// Each action does an optimistic `replaceDealProp` patch,
// fires a fetch, and reverts on 422 / network failure.
// The host page's ActionCable subscription reconciles any
// stale fields when the server confirms.

interface UseDealContactsOptions {
  deal: DealDrawerData
  dealPropKey: DealPropKey
}

interface UseDealContactsResult {
  // Add a company contact to the roster.
  addContact: (contactId: number, role?: string) => void
  // Update the role label on an existing roster member.
  setRole: (contactId: number, role: string) => void
  // Make a roster member the primary contact. Reorders
  // the list (primary first) optimistically.
  makePrimary: (contactId: number) => void
  // Remove a non-primary contact from the roster.
  removeContact: (contactId: number) => void
  // True while any mutation is in-flight.
  submitting: boolean
}

export function useDealContacts({
  deal,
  dealPropKey,
}: UseDealContactsOptions): UseDealContactsResult {
  const [submitting, setSubmitting] = useState(false)

  const mutate = useCallback(
    (
      optimistic: DealDrawerData,
      url: string,
      method: string,
      body: Record<string, unknown>,
      revert: DealDrawerData,
    ) => {
      replaceDealProp(dealPropKey, () => optimistic)
      setSubmitting(true)
      fetch(url, {
        method,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        credentials: "same-origin",
        body: JSON.stringify(body),
      })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`)
        })
        .catch(() => {
          replaceDealProp(dealPropKey, () => revert)
        })
        .finally(() => {
          setSubmitting(false)
        })
    },
    [dealPropKey],
  )

  const addContact = useCallback(
    (contactId: number, role?: string) => {
      if (submitting) return
      const candidate = deal.companyContacts.find(
        (c) => c.id === contactId,
      )
      if (!candidate) return

      const newMember: DealDrawerRosterContact = {
        id: candidate.id,
        displayName: candidate.displayName,
        initials: candidate.initials,
        jobTitle: candidate.jobTitle,
        role: role ?? null,
        primary: false,
        email: null,
        phone: null,
        conversationId: null,
      }

      const optimistic: DealDrawerData = {
        ...deal,
        contacts: [...deal.contacts, newMember],
        companyContacts: deal.companyContacts.filter(
          (c) => c.id !== contactId,
        ),
      }

      mutate(
        optimistic,
        `/v2/deals/${deal.id}/contacts`,
        "POST",
        { contact_id: contactId, ...(role ? { role } : {}) },
        deal,
      )
    },
    [deal, mutate, submitting],
  )

  const setRole = useCallback(
    (contactId: number, role: string) => {
      if (submitting) return

      // Normalize blanks to null so clearing a role optimistically
      // matches the server (which stores `.presence` → nil). The
      // PATCH body still ships "" so the backend clears the column.
      const normalized = role.trim() || null

      const optimistic: DealDrawerData = {
        ...deal,
        contacts: deal.contacts.map((c) =>
          c.id === contactId ? { ...c, role: normalized } : c,
        ),
      }

      mutate(
        optimistic,
        `/v2/deals/${deal.id}/contacts/${contactId}`,
        "PATCH",
        { role: normalized ?? "" },
        deal,
      )
    },
    [deal, mutate, submitting],
  )

  const makePrimary = useCallback(
    (contactId: number) => {
      if (submitting) return
      const target = deal.contacts.find((c) => c.id === contactId)
      if (!target || target.primary) return

      const updated = deal.contacts
        .map((c) => ({
          ...c,
          primary: c.id === contactId,
        }))
        .sort((a, b) => {
          if (a.primary && !b.primary) return -1
          if (!a.primary && b.primary) return 1
          return a.displayName.localeCompare(b.displayName)
        })

      const optimistic: DealDrawerData = {
        ...deal,
        contacts: updated,
      }

      mutate(
        optimistic,
        `/v2/deals/${deal.id}/contacts/${contactId}`,
        "PATCH",
        { primary: true },
        deal,
      )
    },
    [deal, mutate, submitting],
  )

  const removeContact = useCallback(
    (contactId: number) => {
      if (submitting) return
      const contacts = deal.contacts ?? []
      const target = contacts.find((c) => c.id === contactId)
      if (!target || target.primary) return

      // Re-add to the picker candidates ONLY when the deal has
      // a company (the candidate list is company-scoped) and the
      // contact isn't already listed — otherwise we'd flash an
      // invalid/duplicate candidate. Realtime reconciles the rest.
      const companyContacts = deal.companyContacts ?? []
      const alreadyCandidate = companyContacts.some(
        (c) => c.id === target.id,
      )
      const nextCompanyContacts =
        deal.company && !alreadyCandidate
          ? [
              ...companyContacts,
              {
                id: target.id,
                displayName: target.displayName,
                initials: target.initials,
                jobTitle: target.jobTitle,
              },
            ].sort((a, b) => a.displayName.localeCompare(b.displayName))
          : companyContacts

      const optimistic: DealDrawerData = {
        ...deal,
        contacts: contacts.filter((c) => c.id !== contactId),
        companyContacts: nextCompanyContacts,
      }

      mutate(
        optimistic,
        `/v2/deals/${deal.id}/contacts/${contactId}`,
        "DELETE",
        {},
        deal,
      )
    },
    [deal, mutate, submitting],
  )

  return { addContact, setRole, makePrimary, removeContact, submitting }
}
